import { formatDate } from '../utils/dateConversion'
import { getFormattedDateByGranularity } from '../utils/formatChartDates'

const handleUserOnboardingPreferences = ({ data, status }) => {
  const {
    user_shift,
    sleep_start,
    sleep_end,
    caffeine_products,
    caffeine_preferences,
    caffeine_start,
  } = data

  const shiftWithNotMyShift = [
    {
      value: user_shift.id,
      label: user_shift.name,
    },
    {
      value: 'shiftIncorrect',
      label: 'my shift is not correct',
    },
  ]

  const newCaffeineProducts = caffeine_products.map(product => {
    return {
      id: product.id,
      name: product.name,
      description: product.description,
      imgTag: product.product_image_tag,
      isNoCaffeine: product?.list_index ? true : false,
    }
  })

  const noCaffeineProduct = newCaffeineProducts.find(
    product => product.isNoCaffeine,
  )
  const filterNoCaffeineProduct = newCaffeineProducts.filter(
    product => !product.isNoCaffeine,
  )
  const ordenedCaffeineProducts = [
    noCaffeineProduct,
    ...filterNoCaffeineProduct,
  ]

  const formattedData = {
    shift: shiftWithNotMyShift,
    sleepTime: sleep_start ? new Date(sleep_start) : null,
    awakeTime: sleep_end ? new Date(sleep_end) : null,
    caffeineProducts: ordenedCaffeineProducts,
    caffeinePreferences: caffeine_preferences,
    caffeineTime: caffeine_start ? new Date(caffeine_start) : null,
  }

  return {
    status,
    data: formattedData,
  }
}

const handleUserPreferences = ({ data, status }) => {
  const {
    sleep_start,
    sleep_end,
    caffeine_products,
    caffeine_preferences,
    caffeine_start,
  } = data

  const newCaffeineProducts = caffeine_products.map(product => {
    return {
      id: product.id,
      name: product.name,
      imgTag: product.product_image_tag,
      isNoCaffeine: product?.list_index ? true : false,
      description: product.description,
      caffeineMg: product.caffeine_mg,
    }
  })

  const noCaffeineProduct = newCaffeineProducts.find(
    product => product.isNoCaffeine,
  )
  const filterNoCaffeineProduct = newCaffeineProducts.filter(
    product => !product.isNoCaffeine,
  )
  const ordenedCaffeineProducts = [
    noCaffeineProduct,
    ...filterNoCaffeineProduct,
  ]

  const newCaffeinePreferences = caffeine_preferences.map(
    preference => preference.id,
  )

  const formattedData = {
    sleepTime: sleep_start ? new Date(sleep_start) : null,
    awakeTime: sleep_end ? new Date(sleep_end) : null,
    caffeineProducts: ordenedCaffeineProducts,
    caffeinePreferences: newCaffeinePreferences,
    caffeineTime: caffeine_start ? new Date(caffeine_start) : null,
  }

  return {
    status,
    data: formattedData,
  }
}

const handleSaveUserIntake = ({
  sleepTime,
  awakeTime,
  caffeinePreferences,
  caffeineTime,
}) => {
  const normalizeCaffeinePreferences = caffeinePreferences?.map(preference => {
    return {
      id: preference.id,
      name: preference.name,
      product_image_tag: preference.imgTag,
      list_index: preference.isNoCaffeine,
      caffeine_mg: preference.caffeineMg,
      count: preference.count,
    }
  })

  return {
    sleep_start: sleepTime,
    sleep_end: awakeTime,
    caffeine_products: normalizeCaffeinePreferences,
    caffeine_start: caffeineTime,
  }
}

const handleUpdateIntake = ({
  id,
  type,
  sleepTime,
  awakeTime,
  caffeinePreferences,
  caffeineTime,
}) => {
  const normalizeCaffeinePreferences = caffeinePreferences?.map(preference => {
    return {
      id: preference.id,
      name: preference.name,
      product_image_tag: preference.imgTag,
      list_index: preference.isNoCaffeine,
      caffeine_mg: preference.caffeineMg,
      count: preference.count,
    }
  })

  return {
    id,
    type,
    sleep_start: sleepTime,
    sleep_end: awakeTime,
    caffeine_products: normalizeCaffeinePreferences,
    caffeine_start: caffeineTime,
  }
}

const handleHistoricalMetrics = ({ data }) => {
  const namesCamelCase = data?.map(({ message, value, list_index }) => {
    return {
      message,
      value,
      listIndex: list_index,
    }
  })

  return namesCamelCase.sort((a, b) => a.listIndex - b.listIndex)
}

const handleDashboardKPIs = ({ data }) => {
  const namesCamelCase = data?.map(
    ({
      title,
      kpi,
      type,
      comparison,
      text_comparison,
      trend_style,
      comparison_type,
    }) => {
      return {
        title,
        kpi,
        type,
        comparison,
        textComparison: text_comparison,
        trendStyle: trend_style,
        comparisonType: comparison_type,
      }
    },
  )

  return namesCamelCase
}

const handleGetColumns = ({ data }) => {
  const {
    id,
    employees,
    high_fatigue,
    notification,
    counter_measure,
    fatigue_level_8h,
    intake_pending,
  } = data

  return {
    id,
    employees,
    highFatigue: high_fatigue,
    notification,
    counterMeasure: counter_measure,
    fatigueLevel8h: fatigue_level_8h,
    intakePending: intake_pending,
  }
}

const handleVisualManagementUsersData = ({ data, graphMessage }) => {
  const namesCamelCase = data?.map(
    ({
      id,
      colaborator,
      time_till_alert,
      notification_status,
      counter_measure_status,
      shift,
      position,
      team,
      intake_pending,
      data: predictionData,
    }) => {
      return {
        id,
        colaborator,
        timeTillAlert: time_till_alert,
        notificationStatus: notification_status?.toLowerCase(),
        counterMeasureStatus: counter_measure_status?.toLowerCase(),
        shift,
        position: {
          level: position.level,
          title: position.title,
        },
        team,
        intakePending: intake_pending,
        data: predictionData?.map(
          ({
            timestamp,
            prediction,
            prediction_result,
            is_sleeping,
            is_working,
          }) => {
            return {
              timestamp: timestamp,
              prediction: prediction,
              predictionResult: Math.round(prediction_result),
              isSleeping: is_sleeping,
              isWorking: is_working,
            }
          },
        ),
      }
    },
  )

  return {
    data: namesCamelCase,
    graphMessage: {
      totalHours: graphMessage.total_hours || 0,
      totalUsers: graphMessage.total_users || 0,
    },
  }
}

const handleUserPlan = ({ data }) => {
  const {
    id,
    name,
    trial_period_days,
    value,
    created_at,
    updated_at,
    default_features,
  } = data

  return {
    id,
    name,
    trialPeriodDays: trial_period_days,
    value,
    createAt: created_at,
    updatedAt: updated_at,
    defaultFeatures: {
      predictionDuration: default_features.prediction_duration,
      pvtAccess: default_features.pvt_access,
      userDashboard: default_features.user_dashboard,
    },
  }
}

const handleNotifications = ({ data }) => {
  const normalizedFields = data?.map(
    ({
      user_id,
      user_name,
      message,
      severity_level,
      status,
      sent_at,
      shift,
      position,
      team,
      id,
    }) => ({
      user_id,
      employee: user_name,
      message,
      date: sent_at,
      status,
      severity: severity_level,
      shift,
      position: {
        title: position?.title,
        level: position?.level,
      },
      team,
      id,
    }),
  )

  return normalizedFields
}

const handleInterventions = ({ data }) => {
  const normalizedFields = data?.map(
    ({
      user_id,
      user_name,
      message,
      sent_at,
      status,
      severity_level,
      id,
    }) => ({
      user_id,
      employee: user_name,
      message,
      date: sent_at,
      status,
      severity: severity_level,
      id,
    }),
  )

  return normalizedFields
}

const handleSystemEvents = ({ data }) => {
  const normalizedFields = data?.map(
    ({ id, date, type, action, user, event_type }) => ({
      id,
      date,
      type,
      action,
      user,
      eventType: event_type,
    }),
  )

  return normalizedFields
}

const handleReportOptimizeCounterMeasure = ({ data, graphMessage }) => {
  const namesCamelCase = data?.map(
    ({ id, colaborator, shift, position, team, data: predictionData }) => {
      return {
        id,
        colaborator,
        shift,
        position: {
          level: position.level,
          title: position.title,
        },
        team,
        data: predictionData?.map(
          ({
            timestamp,
            prediction,
            prediction_result,
            optimization_result,
            is_sleeping,
            is_working,
          }) => {
            return {
              timestamp: timestamp,
              prediction: prediction,
              predictionResult: Math.round(prediction_result),
              optimizationResult: Math.round(optimization_result),
              isSleeping: is_sleeping,
              isWorking: is_working,
            }
          },
        ),
      }
    },
  )

  return {
    data: namesCamelCase,
    graphMessage: {
      totalHours: graphMessage.total_hours || 0,
      totalUsers: graphMessage.total_users || 0,
      totalPrediction: graphMessage.total_prediction || 0,
      totalOptimization: graphMessage.total_optimization || 0,
    },
  }
}

const handleSupervisorNotifications = ({ data }) => {
  const namesCamelCase = data?.map(
    ({ action, image, link, text, type, custom_message }) => {
      return {
        action,
        image,
        link,
        text,
        type,
        customMessage: custom_message,
      }
    },
  )

  return namesCamelCase
}

const handlePvtStarted = ({ data }) => {
  const { pvt_id } = data

  return { pvtId: pvt_id }
}

const handlePvtStatus = ({ data }) => {
  const { status, blocked_until } = data

  return {
    status,
    blockedUntil: blocked_until,
  }
}

const handleUserPendingActions = ({ data }) => {
  const {
    name,
    onboarding_pending,
    pvt_pending,
    intake_pending,
    missing_pvt_count,
  } = data

  return {
    name,
    onboardingPending: onboarding_pending,
    pvtPending: pvt_pending,
    intakePending: intake_pending,
    missingPvtCount: missing_pvt_count,
  }
}

const handleUnreadNotificationCount = ({ data }) => {
  const { notifications_count } = data

  return {
    notificationCount: notifications_count,
  }
}

const handleGetImport = ({ data }) => {
  const namesCamelCase = data?.map(
    ({
      date,
      status,
      created_by,
      import_created_count,
      import_updated_count,
      import_errors_count,
      id,
    }) => {
      return {
        date,
        status,
        createdBy: created_by,
        importCreatedCount: import_created_count,
        importUpdatedCount: import_updated_count,
        importErrorCount: import_errors_count,
        id
      }
    },
  )

  return namesCamelCase
}

const handleAccountConfig = ({ data }) => {
  const { config, users_count, status, payment_status, users_limit, caffeine_products } = data

  return {
    config,
    userCount: users_count,
    usersLimit: users_limit,
    accountStatus: status,
    paymentStatus: payment_status,
    caffeineProducts: caffeine_products,
    availableCaffeineProducts: data.available_caffeine_products || [],
  }
}

const handleIntakeList = ({ data }) => {
  const caffeineIntakes = data?.caffeine_intakes.map(
    ({ id, caffeine_start, caffeine_products, created_by }) => {
      return {
        id,
        caffeineTaken: caffeine_start,
        caffeineProducts: caffeine_products,
        createdBy: created_by,
      }
    },
  )

  const sleepIntakes = data?.sleep_intakes.map(
    ({ id, sleep_end, sleep_start, created_by }) => {
      return {
        id,
        sleepEnd: sleep_end,
        sleepStart: sleep_start,
        createdBy: created_by,
      }
    },
  )

  const namesCamelCase = {
    caffeineIntakes: caffeineIntakes || [],
    sleepIntakes: sleepIntakes || [],
  }

  return namesCamelCase
}

const handlePredictionHistory = ({ data, graphMessage }) => {
  return {
    data,
    graphMessage: {
      totalHours: graphMessage.total_hours || 0,
      totalUsers: graphMessage.total_users || 0,
    },
  }
}

const handleDefaultParams = ({ date, selectedFilterOptions }) => {
  const shifts = selectedFilterOptions['shifts']?.join(',') || null
  const teams = selectedFilterOptions['teams']?.join(',') || null
  const titles = selectedFilterOptions['positions']?.join(',') || null
  const levels = selectedFilterOptions['levels']?.join(',') || null

  return {
    start_date: formatDate({ date: date ? date[0] : '' }),
    end_date: formatDate({ date: date ? date[1] : '' }),
    shifts,
    teams,
    titles,
    levels,
  }
}

const handleShiftFatigue = ({ data, graphMessage }) => {
  const statuses = ['bad', 'regular', 'good']
  const categories = []
  const output = []

  statuses.forEach(status => {
    const predictionData = data.map(shift =>
      Math.round(shift.prediciton_result[status]),
    )
    const optimizedData = data.map(shift =>
      Math.round(shift.optimization_result[status]),
    )

    output.push({
      name: status + 'pred',
      group: 'prediction',
      data: predictionData,
    })
    output.push({
      name: status + 'opt',
      group: 'optimized',
      data: optimizedData,
    })
  })

  data.map(shift => categories.push(shift.shift_name))

  return {
    categories,
    output,
    graphMessage: {
      totalHours: graphMessage.total_hours || 0,
      totalUsers: graphMessage.total_users || 0,
    },
  }
}

const handleFatigueEvolution = ({ data, granularity, graphMessage }) => {
  const statuses = ['bad', 'regular', 'good']
  const output = []
  const categories = getFormattedDateByGranularity({
    periods: data,
    granularity,
  })

  statuses.forEach(status => {
    const predictionData = data.map(shift => shift.prediction_result[status])

    output.push({
      name: status + 'pred',
      group: 'prediction',
      data: predictionData,
    })
  })

  return {
    categories,
    output,
    graphMessage: {
      totalHours: graphMessage.total_hours || 0,
      totalUsers: graphMessage.total_users || 0,
    },
  }
}

const handleSleepQuantity = ({ data, graphMessage }) => {
  const dataPoints = data.map(item => {
    if (!item.grouping)
      return {
        x: 'Não identificado',
        y: [item.minutes],
      }

    return {
      x: item.grouping,
      y: [item.minutes],
    }
  })

  return {
    data: [
      {
        name: 'Horas de sono',
        data: dataPoints,
      },
    ],
    graphMessage: {
      totalHours: graphMessage.total_hours || 0,
      totalUsers: graphMessage.total_users || 0,
    },
  }
}

const handleDefaultFilters = ({ data }) => {
  const shifts = data.filters?.shift.filter(item => {
    if (item != null) return item
  })

  const teams = data.filters?.team.filter(item => {
    if (item != null) return item
  })

  const positions = data.filters?.position?.title.filter(item => {
    if (item != null) return item
  })

  const levels = data.filters?.position?.level.filter(item => {
    if (item != null) return item
  })

  return {
    shifts: shifts || [],
    teams: teams || [],
    positions: positions || [],
    levels: levels || [],
  }
}

const handleDistributionFatigue = ({ data, graphMessage }) => {
  const dataSorted = data.sort((a, b) => b.value - a.value)

  const columnData = dataSorted.map(data => Math.round(data.value))
  const lineData = dataSorted.map(data => data.target)
  const labels = dataSorted.map(data => data.name)

  return {
    data: [
      {
        name: 'Colaboradores(as)',
        type: 'column',
        data: columnData,
        color: '#E27837',
      },
      { name: 'Benchmark', type: 'line', data: lineData, color: '#4CB7BA' },
    ],
    labels,
    graphMessage: {
      totalHours: graphMessage.total_hours || 0,
      totalUsers: graphMessage.total_users || 0,
    },
  }
}

const handleAdminCockpitData = ({ data }) => {
  const { config, user_count, account_count, account_list, current_account, plans } =
    data

  return {
    config,
    userCount: user_count,
    accountCount: account_count,
    accountList: account_list,
    currentAccount: current_account,
    plans
  }
}

const handleUserFullList = ({ data }) => {
  const { level_count, users } = data

  return {
    levelCount: level_count,
    userList: users,
  }
}

const handleInputDataQuality = ({ data }) => {
  const { data_message, user_data, headers } = data

  return {
    dataMessage: data_message,
    data: user_data,
    headers,
  }
}

const handleAccountChangeData = ({ data }) => {
  const { account_list, current_account } =
    data

  return {
    accountList: account_list,
    currentAccount: current_account
  }
}

export const AdapterService = {
  handleUserOnboardingPreferences,
  handleUserPreferences,
  handleSaveUserIntake,
  handleHistoricalMetrics,
  handleDashboardKPIs,
  handleGetColumns,
  handleVisualManagementUsersData,
  handleUserPlan,
  handleNotifications,
  handleSystemEvents,
  handleReportOptimizeCounterMeasure,
  handleSupervisorNotifications,
  handlePvtStatus,
  handlePvtStarted,
  handleUserPendingActions,
  handleUnreadNotificationCount,
  handleGetImport,
  handleAccountConfig,
  handleIntakeList,
  handleUpdateIntake,
  handlePredictionHistory,
  handleShiftFatigue,
  handleFatigueEvolution,
  handleSleepQuantity,
  handleDefaultParams,
  handleDefaultFilters,
  handleDistributionFatigue,
  handleAdminCockpitData,
  handleUserFullList,
  handleInputDataQuality,
  handleInterventions,
  handleAccountChangeData,
}
